/**
 * Read-only Tax History V2 API service.
 *
 * V2 has a different form/answer contract from TaxForm, so this service stays
 * separate from taxHistoryApi.js even though the URL shape is similar.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';
const API_STATUS_SUCCESS = 0;

const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) return data;

    const error = new Error(body.message || body.Message || 'Request failed');
    error.status = status;
    error.data = data;
    throw error;
  }
  return body;
}

function toError(error) {
  const body = error && error.response && error.response.data;
  const result = new Error(
    (body && (body.message || body.Message)) ||
      (error && error.message) ||
      'Request failed'
  );
  result.httpStatus = error && error.response && error.response.status;
  result.status = body && (body.status ?? body.Status);
  result.data = body && (body.data ?? body.Data);
  return result;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (error) {
    throw error instanceof Error && error.httpStatus === undefined && error.status !== undefined
      ? error
      : toError(error);
  }
}

async function post(url, body) {
  try {
    return unwrap(await http.post(url, body));
  } catch (error) {
    throw error instanceof Error && error.httpStatus === undefined && error.status !== undefined
      ? error
      : toError(error);
  }
}

/**
 * #776 — file download. Not routed through get()/toError(): with responseType 'blob' the error body is a Blob too,
 * so its JSON message has to be read back here. `error.code` = the API's 400 code (EXPORT_EMPTY / EXPORT_TOO_MANY /
 * INVALID_DATE_RANGE) or null when the body is not our JSON (502 HTML from the front door, network error).
 */
async function getBlob(url) {
  try {
    const response = await http.get(url, { responseType: 'blob', headers: { Accept: '*/*' } });
    return response.data;
  } catch (error) {
    const data = error?.response?.data;
    let message = null;
    if (data instanceof Blob) {
      try {
        const body = JSON.parse(await data.text());
        message = body?.message || body?.Message || null;
      } catch {
        message = null;
      }
    }
    const result = new Error(message || error?.message || 'Request failed');
    result.code = message;
    result.httpStatus = error?.response?.status;
    throw result;
  }
}

function queryString(params) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '' || value === false) return;
    query.set(key, value);
  });
  const text = query.toString();
  return text ? `?${text}` : '';
}

const ROOT = 'api/v1/TaxFormV2';

export const taxHistoryV2Api = {
  /**
   * @param {{yearId?: number|string, status?: string, sessionId?: string,
   * userId?: string, search?: string, page?: number, pageSize?: number,
   * includePlayground?: boolean}} [filter]
   */
  list(filter = {}) {
    return get(
      `${ROOT}${queryString({
        yearId: filter.yearId,
        status: filter.status,
        sessionId: filter.sessionId,
        userId: filter.userId,
        search: filter.search,
        page: Number(filter.page) || 1,
        pageSize: Number(filter.pageSize) || 50,
        includePlayground: filter.includePlayground === true ? true : undefined,
        startDate: filter.startDate,
        endDate: filter.endDate,
      })}`
    );
  },

  /**
   * #776 — ZIP of one .xlsx per form, same filters as {@link list} (paging ignored). Each entry holds the same
   * rows as the file {@link exportFormCsv} gives for that form, styled like the old Export Excel.
   * @returns {Promise<Blob>}
   */
  exportZip(filter = {}) {
    return getBlob(`${ROOT}/export${queryString({
      yearId: filter.yearId,
      status: filter.status,
      search: filter.search,
      includePlayground: filter.includePlayground === true ? true : undefined,
      startDate: filter.startDate,
      endDate: filter.endDate,
      lang: filter.lang,
    })}`);
  },

  /**
   * #776 — one form's answer CSV (No., Form, Question, Answer, Updated), built by the same API writer as the ZIP.
   * @returns {Promise<Blob>}
   */
  exportFormCsv(formId, lang) {
    return getBlob(`${ROOT}/${encodeURIComponent(formId)}/export${queryString({ lang })}`);
  },

  /** @returns {Promise<Object>} { form, answers, configForm } */
  getById(formId) {
    return get(`${ROOT}/${encodeURIComponent(formId)}`);
  },

  /** Consent-link style, card button labels and card date caption templates returned by LHB Tax/GetTaxHistory.
   * `createdDateTemplate` / `updatedDateTemplate` hold the stored templates ('' = default);
   * `defaultCreatedDateTemplate` / `defaultUpdatedDateTemplate` are the defaults (response only — the dialog's placeholders).
   * @returns {Promise<{consentLinkWording: {th: string, en: string, zh: string},
   * consentLinkWordingColor: string, consentLinkUnderline: boolean, buttonLabels: Array<Object>,
   * createdDateTemplate: {th: string, en: string, zh: string}, updatedDateTemplate: {th: string, en: string, zh: string},
   * defaultCreatedDateTemplate: {th: string, en: string, zh: string},
   * defaultUpdatedDateTemplate: {th: string, en: string, zh: string}}>} */
  getStyleConfig() {
    return get('api/v1/TaxHistoryStyle');
  },

  saveStyleConfig(body) {
    return post('api/v1/TaxHistoryStyle/save', body);
  },

  /** #882 — renders card date caption templates with the current time (Thai time) by the same renderer the
   * customer API uses; nothing is saved. A null field or a blank language = that language's default.
   * @param {{createdDateTemplate?: {th: string, en: string, zh: string},
   * updatedDateTemplate?: {th: string, en: string, zh: string}}} body
   * @returns {Promise<{createdText: {th: string, en: string, zh: string}, createdError: {th: string, en: string, zh: string},
   * updatedText: {th: string, en: string, zh: string}, updatedError: {th: string, en: string, zh: string}}>}
   * error '' = rendered fine; non-empty = that field cannot be rendered (its text is ''). */
  previewDateTemplates(body) {
    return post('api/v1/TaxHistoryStyle/preview', body);
  },
};

export default taxHistoryV2Api;
